import React, { useCallback, useMemo } from 'react';
import { components, SingleValueProps } from 'react-select';
import { ReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import {
  FilterType,
  IFieldFieldsFragment,
  INamedFilterFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { colors } from '../../../config/styles';
import { BasicEntity, NamedFilterLabel } from '../../EntityLabels';
import { FilterOptionOption, IReactSelectOption } from '../../../utils/types';
import { theme } from '../../../styles/theme';

interface IProps<FieldType, NamedFilterType> {
  filterFieldOptions: FieldType[];
  namedFilterOptions: NamedFilterType[];
  selected: FieldType | NamedFilterType | null;
  onChange: (selectedOption: FieldType | NamedFilterType | null) => void;
  searchText: string;
  onSearchChange: (searchText: string) => void;
  isError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  isLoading?: boolean;
  operandTypeIsField?: boolean;
  maxWidth?: number;
  maxMenuHeight?: number;
}

interface IMenuOptionProps {
  optionType: FilterType;
}

interface IMenuOption extends IReactSelectOption, IMenuOptionProps {}

const NamedFilterDropdown = <
  FieldType extends Omit<IFieldFieldsFragment, 'description'>,
  NamedFilterType extends INamedFilterFieldsFragment,
>({
  filterFieldOptions,
  namedFilterOptions,
  selected,
  onChange,
  searchText,
  onSearchChange,
  isError = false,
  placeholder = '',
  disabled = false,
  autoFocus = false,
  isLoading = false,
  operandTypeIsField,
  maxWidth,
  maxMenuHeight,
}: IProps<FieldType, NamedFilterType>) => {
  const mapFilterOption = useCallback(
    (filter: FieldType): IMenuOption => ({
      optionType: FilterType.Basic,
      value: filter.key,
      label: filter.name,
    }),
    [],
  );

  const mapNamedFilterOption = useCallback(
    (namedFilter: NamedFilterType): IMenuOption => ({
      optionType: FilterType.Named,
      value: namedFilter.id,
      label: namedFilter.name,
    }),
    [],
  );

  const options: IMenuOption[] = useMemo(
    () =>
      [
        ...namedFilterOptions.map((option) => mapNamedFilterOption(option)),
        ...filterFieldOptions.map((option) => mapFilterOption(option)),
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [
      filterFieldOptions,
      mapFilterOption,
      mapNamedFilterOption,
      namedFilterOptions,
    ],
  );

  const renderValueLabel = (props: SingleValueProps<IMenuOption, false>) => {
    const { data } = props;

    if (!selected) {
      return (
        <components.SingleValue {...props}>
          {props.children}
        </components.SingleValue>
      );
    }

    if (selected.type === FilterType.Named) {
      return (
        <components.SingleValue {...props}>
          <NamedFilterLabel
            namedFilter={selected}
            onRemoveNamedFilter={() => onChange(null)}
          />
        </components.SingleValue>
      );
    }

    return (
      <components.SingleValue {...props}>
        <BasicEntity
          onClear={() => onChange(null)}
          variant={operandTypeIsField ? 'purple' : 'blue'}
        >
          {data.label}
        </BasicEntity>
      </components.SingleValue>
    );
  };

  const getCurrentValue = () => {
    if (!selected) {
      return null;
    }

    if (selected.type === FilterType.Named) {
      return mapNamedFilterOption(selected as NamedFilterType);
    }

    return mapFilterOption(selected as FieldType);
  };

  const handleFilterOptions = (
    option: FilterOptionOption<IMenuOptionProps>,
  ) => {
    const currentValue = getCurrentValue();

    if (!currentValue) {
      return true;
    }

    return currentValue.optionType === option.data.optionType;
  };

  const handleChange = (newOption: IMenuOption | null) => {
    if (!newOption) {
      onChange(newOption);

      return;
    }

    if (newOption.optionType === FilterType.Named) {
      onChange(
        namedFilterOptions?.find(
          (namedFilter) => namedFilter.id === newOption.value,
        ) ?? null,
      );

      return;
    }

    if (newOption.optionType === FilterType.Basic) {
      onChange(
        filterFieldOptions?.find((filter) => filter.key === newOption.value) ??
          null,
      );
    }
  };

  return (
    <ReactSelectMenu<IMenuOption>
      closeMenuOnSelect
      onChange={handleChange}
      value={getCurrentValue()}
      blurInputOnSelect
      isMulti={false}
      autoFocus={autoFocus}
      openMenuOnFocus={autoFocus}
      options={options}
      filterOption={handleFilterOptions}
      inputValue={searchText}
      onInputChange={onSearchChange}
      components={{
        SingleValue: renderValueLabel,
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      isLoading={isLoading}
      maxMenuHeight={maxMenuHeight}
      styles={{
        control: (base) => {
          const styles = { ...base };

          if (maxWidth) {
            styles.maxWidth = `${maxWidth}px`;
          }

          if (isError) {
            return {
              ...styles,
              borderColor: colors.mainError,
            };
          }

          return styles;
        },
        option: (base, props) => {
          if (props.data.optionType === FilterType.Named) {
            return {
              ...base,
              color: theme.colors.secondary900,
            };
          }

          return base;
        },
      }}
    />
  );
};

export default NamedFilterDropdown;
